import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { BoxProps } from '@chakra-ui/react';
import isEmpty from 'lodash/isEmpty';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import TabElement from 'components/Table/Table';
import Thead from 'components/Table/Thead';
import Tbody from 'components/Table/Tbody';
import Th from 'components/Table/Th';
import columns from 'pages/OnboardScreens/Listing/components/Table/columns';
import EmptyBody from 'components/Table/EmptyBody';
import TableWrapper from 'components/Table/TableWrapper';
import { API, APIRoutes } from 'api';
import DraggableRow from 'pages/OnboardScreens/Listing/components/Table/DraggableRow';

type TableProps = BoxProps & {
  data: OnboardScreen[];
};

function Table({ data, ...props }: TableProps) {
  const queryClient = useQueryClient();
  const table = useReactTable({
    data,
    columns: columns(),
    getCoreRowModel: getCoreRowModel(),
  });

  const noTableData = isEmpty(data);

  const { mutate: reorderOnboardScreen } = useMutation(
    (payload: { id: number; targetRowIndex: number }) =>
      API.put(APIRoutes.onboardScreens.sort(payload.id), {
        position: payload.targetRowIndex,
      }),
    {
      mutationKey: 'onboardScreensMutation',
      onSuccess: () => {
        void queryClient.invalidateQueries('onboardScreens');
      },
      onError: () => {
        toast.error('Wystąpił problem podczas zmiany kolejności');
      },
    },
  );

  const { mutate: removeOnboardScreen } = useMutation((id: number) => API.delete(APIRoutes.onboardScreens.byId(id)), {
    mutationKey: 'onboardScreensMutation',
    onSuccess: () => {
      toast.success('Pomyślnie usunięto ekran');
      void queryClient.invalidateQueries('onboardScreens');
    },
    onError: () => {
      toast.error('Wystąpił problem podczas usuwania ekranu');
    },
  });

  return (
    <TableWrapper {...props}>
      <TabElement isEmpty={noTableData}>
        <Thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={`header_row_${headerGroup.id}`}>
              {headerGroup.headers.map((header) => (
                <Th key={`header_cell_${header.id}`}>
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                </Th>
              ))}
            </tr>
          ))}
        </Thead>
        <Tbody>
          {noTableData ? (
            <EmptyBody columnAmount={table.getAllColumns().length} />
          ) : (
            table
              .getRowModel()
              .rows.map((row) => (
                <DraggableRow
                  key={row.id}
                  row={row}
                  handleReorder={reorderOnboardScreen}
                  handleDelete={removeOnboardScreen}
                  screenId={row.original.id}
                />
              ))
          )}
        </Tbody>
      </TabElement>
    </TableWrapper>
  );
}

export default Table;
